import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Eye, EyeOff, Globe2, Mail, Lock, ArrowRight, ShieldCheck, Building2,
  Users, CalendarCheck, Wallet, Star, UserPlus, Sparkles, Map, Activity,
  TrendingUp,
} from "lucide-react";
import { useTenantBranding } from "@/lib/appMode";

const FEATURE_CHIPS = [
  { label: "HR & Core",     icon: Users         },
  { label: "Attendance",    icon: CalendarCheck },
  { label: "Payroll",       icon: Wallet        },
  { label: "Performance",   icon: Star          },
  { label: "Recruitment",   icon: UserPlus      },
  { label: "AI Workflows",  icon: Sparkles      },
];

export default function Login() {
  const { token, login, loading } = useAuth();
  const { branding: tBranding, tenant } = useTenantBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lang, setLang] = useState("en-US");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await login(email, password); toast.success("Welcome back"); nav("/"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setSubmitting(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const api = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${api}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success("If that email exists, a reset link has been sent.");
      setForgotOpen(false); setForgotEmail("");
    } catch { toast.error("Could not send reset link"); }
    finally { setForgotBusy(false); }
  };

  if (loading) return null;
  if (token) return <Navigate to="/" replace />;

  const tenantName = tenant?.name && tenant.id !== "default" ? tenant.name : null;

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #f5f7ff 0%, #ffffff 40%, #fdf4ff 100%)",
      }}
      data-testid="login-page"
    >
      {/* Decorative background orbs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-violet-300/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-fuchsia-300/20 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 w-72 h-72 rounded-full bg-blue-200/25 blur-3xl pointer-events-none" />

      {/* Language selector — top right */}
      <div className="absolute top-6 right-6 z-20">
        <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-slate-200 rounded-full px-3 py-1.5 shadow-sm">
          <Globe2 className="w-3.5 h-3.5 text-slate-500" />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="text-xs font-semibold text-slate-700 bg-transparent outline-none cursor-pointer"
            data-testid="lang-selector"
          >
            <option value="en-US">English (US)</option>
            <option value="en-GB">English (UK)</option>
            <option value="en-IN">English (India)</option>
          </select>
        </div>
      </div>

      <div className="relative grid lg:grid-cols-[1.05fr_minmax(0,440px)] xl:grid-cols-[1.15fr_minmax(0,460px)] gap-8 lg:gap-12 max-w-[1400px] mx-auto px-6 lg:px-12 py-10 min-h-screen">
        {/* ========== LEFT — Brand + visual story ========== */}
        <div className="flex flex-col justify-center pt-16 lg:pt-0">
          {/* Logo + Tagline */}
          <div className="flex items-start gap-3.5">
            <img
              src="/qrewhr-logo.jpg"
              alt="Qrew HR"
              className="w-16 h-16 rounded-2xl object-cover ring-1 ring-slate-200 shadow-sm"
            />
            <div className="pt-1">
              <div className="text-3xl font-bold tracking-tight text-slate-900 leading-none">Qrew HR</div>
              <div className="text-xs font-medium text-slate-500 mt-1.5">Smart HR. Stronger Teams.</div>
            </div>
          </div>

          {/* Headline */}
          <h1 className="mt-10 text-[40px] sm:text-[52px] xl:text-[60px] font-bold leading-[1.05] tracking-tight text-slate-900">
            Run your global<br />
            workforce from<br />
            one{" "}
            <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
              smart HR platform.
            </span>
          </h1>

          {/* Subtext */}
          <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-[520px]">
            Manage HR, attendance, payroll, performance, recruitment, communication, and AI workflows in one unified platform—built for global teams that want to move faster.
          </p>

          {/* Feature chips */}
          <div className="mt-8 flex flex-wrap gap-3 max-w-[560px]">
            {FEATURE_CHIPS.map((f) => (
              <div
                key={f.label}
                className="group flex flex-col items-center gap-1.5 min-w-[78px]"
                data-testid={`feature-chip-${f.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center group-hover:border-violet-300 transition">
                  <f.icon className="w-5 h-5 text-slate-700" />
                </div>
                <div className="text-[11px] font-medium text-slate-600">{f.label}</div>
              </div>
            ))}
          </div>

          {/* Dashboard preview cards */}
          <div className="mt-10 grid grid-cols-2 gap-3 max-w-[560px]">
            {/* Global Overview */}
            <div className="col-span-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="preview-global">
              <div className="text-[11px] font-semibold text-slate-700 mb-2">Global Overview</div>
              <div className="flex items-center gap-2 mb-1.5">
                <Map className="w-4 h-4 text-blue-500" />
                <span className="text-xl font-bold text-slate-900">24,751</span>
              </div>
              <div className="text-[10px] text-emerald-600 font-semibold">+12.5% vs last month</div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-[10px]">
                <div><div className="text-slate-500">Countries</div><div className="font-bold text-slate-900">48</div></div>
                <div><div className="text-slate-500">Departments</div><div className="font-bold text-slate-900">32</div></div>
              </div>
            </div>

            {/* Today's Attendance */}
            <div className="col-span-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="preview-attendance">
              <div className="text-[11px] font-semibold text-slate-700 mb-2">Today's Attendance</div>
              <div className="flex items-center gap-3">
                <div className="relative w-14 h-14 shrink-0">
                  <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15" stroke="#E2E8F0" strokeWidth="3" fill="none" />
                    <circle cx="18" cy="18" r="15" stroke="url(#attendGrad)" strokeWidth="3" strokeLinecap="round" fill="none" strokeDasharray="92 100" pathLength="100" />
                    <defs>
                      <linearGradient id="attendGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#2563EB" />
                        <stop offset="100%" stopColor="#06B6D4" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[11px] font-bold text-slate-900">92%</span>
                  </div>
                </div>
                <div className="flex-1 space-y-1 text-[10px]">
                  <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />Present <span className="ml-auto font-semibold">92%</span></div>
                  <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" />Absent <span className="ml-auto font-semibold">5%</span></div>
                  <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />Leave <span className="ml-auto font-semibold">3%</span></div>
                </div>
              </div>
            </div>

            {/* Payroll Summary */}
            <div className="col-span-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="preview-payroll">
              <div className="text-[11px] font-semibold text-slate-700 mb-2">Payroll Summary</div>
              <div className="text-[10px] text-slate-500">Total Payroll · May 2026</div>
              <div className="text-lg font-bold text-slate-900 mt-0.5">$2,45,80,000</div>
              <div className="text-[10px] text-emerald-600 font-semibold mt-1">+8.6% vs Apr</div>
              <div className="mt-2 h-7 flex items-end gap-0.5">
                {[40, 55, 50, 65, 60, 75, 72, 88].map((h, i) => (
                  <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-blue-400 to-violet-500" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>

            {/* AI Insights */}
            <div className="col-span-1 bg-white rounded-2xl border border-violet-200 shadow-sm p-4" data-testid="preview-ai">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-violet-700 mb-2">
                <Sparkles className="w-3 h-3" /> AI Insights
              </div>
              <div className="text-[11px] text-slate-700 leading-relaxed">
                High overtime in <b>Engineering</b>. Consider balancing workload.
              </div>
              <div className="text-[10px] font-semibold text-violet-700 mt-2.5 inline-flex items-center gap-1">
                View recommendation <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </div>
        </div>

        {/* ========== RIGHT — Login card ========== */}
        <div className="flex flex-col justify-center">
          {tenantName && (
            <div className="mb-3 text-center px-4 py-2 rounded-lg bg-violet-50 border border-violet-200 text-xs text-violet-900" data-testid="tenant-banner">
              Logging in to <b>{tenantName}</b>
            </div>
          )}

          <div className="bg-white rounded-3xl shadow-xl border border-slate-200/60 p-8 sm:p-9">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="login-title">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-1.5">Sign in to your Qrew HR workspace</p>

            <form onSubmit={submit} className="space-y-4 mt-7">
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Work Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="w-4 h-4 absolute left-3.5 top-3.5 text-slate-400" />
                  <Input
                    id="email" type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourcompany.com"
                    className="h-12 pl-10 rounded-xl border-slate-200"
                    autoComplete="email"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 absolute left-3.5 top-3.5 text-slate-400" />
                  <Input
                    id="password" type={showPwd ? "text" : "password"} required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="h-12 pl-10 pr-10 rounded-xl border-slate-200"
                    autoComplete="current-password"
                    data-testid="login-password"
                  />
                  <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-3 p-1 text-slate-400 hover:text-slate-700">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-right mt-1.5">
                  <button type="button" onClick={() => setForgotOpen(true)} className="text-xs font-semibold text-violet-700 hover:underline" data-testid="forgot-link">
                    Forgot password?
                  </button>
                </div>
              </div>

              <Button
                type="submit" disabled={submitting}
                className="w-full h-12 text-base font-semibold rounded-xl text-white shadow-lg shadow-violet-500/25 hover:shadow-xl transition-all"
                style={{ background: "linear-gradient(90deg, #2563EB 0%, #6D28D9 50%, #D946EF 100%)" }}
                data-testid="login-submit"
              >
                {submitting ? "Signing in…" : <>Sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-slate-400 font-medium">or continue with</span></div>
            </div>

            {/* Google + Microsoft */}
            <div className="space-y-2.5">
              <button
                type="button" disabled
                onClick={() => toast.info("Google SSO is not configured for this workspace")}
                className="w-full h-11 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center gap-2.5 text-sm font-semibold text-slate-700 transition disabled:opacity-60 disabled:cursor-not-allowed relative"
                data-testid="google-signin"
                title="Google SSO is not configured for this workspace"
              >
                <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
                Sign in with Google
                <span className="absolute right-3 text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wider">Not configured</span>
              </button>
              <button
                type="button" disabled
                onClick={() => toast.info("Microsoft SSO is not configured for this workspace")}
                className="w-full h-11 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center gap-2.5 text-sm font-semibold text-slate-700 transition disabled:opacity-60 disabled:cursor-not-allowed relative"
                data-testid="microsoft-signin"
                title="Microsoft SSO is not configured for this workspace"
              >
                <svg className="w-4 h-4" viewBox="0 0 23 23"><path fill="#f25022" d="M0 0h11v11H0z"/><path fill="#7fba00" d="M12 0h11v11H12z"/><path fill="#00a4ef" d="M0 12h11v11H0z"/><path fill="#ffb900" d="M12 12h11v11H12z"/></svg>
                Sign in with Microsoft
                <span className="absolute right-3 text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wider">Not configured</span>
              </button>
            </div>

            <div className="mt-6 text-center text-sm text-slate-600">
              New to Qrew HR? <Link to="/signup" className="font-semibold text-violet-700 hover:underline" data-testid="signup-link">Start your free trial</Link>
            </div>

            <div className="mt-3 text-center">
              <button type="button" disabled onClick={() => toast.info("Workspace finder is coming soon")} className="text-sm font-semibold text-slate-400 inline-flex items-center gap-1 cursor-not-allowed" data-testid="find-workspace" title="Workspace finder is coming soon">
                <Building2 className="w-3.5 h-3.5" /> Find my workspace
                <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wider ml-1">Coming soon</span>
              </button>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            {[
              { name: "SOC 2",      sub: "Type II"   , icon: ShieldCheck },
              { name: "GDPR",       sub: "Compliant" , icon: Lock        },
              { name: "ISO 27001",  sub: "Certified" , icon: Globe2      },
            ].map((t) => (
              <div key={t.name} className="flex items-center justify-center gap-2 text-slate-700" data-testid={`trust-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <t.icon className="w-4 h-4 text-slate-500" />
                <div className="text-left">
                  <div className="text-[11px] font-bold leading-none">{t.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{t.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-center text-[11px] text-slate-500">Enterprise-grade security. Your data is always protected.</div>
        </div>
      </div>

      {/* Forgot dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Enter your work email and we'll send you a reset link.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForgot} className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Work email</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="pl-9 h-11" data-testid="forgot-email" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={forgotBusy} data-testid="forgot-submit"
                style={{ background: "linear-gradient(90deg, #2563EB 0%, #6D28D9 50%, #D946EF 100%)" }}
                className="text-white">
                {forgotBusy ? "Sending…" : "Send link"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
